import React, { useState, useRef } from 'react';
import { Button, Paper, LinearProgress, Typography, Box, FormControlLabel, Checkbox } from '@mui/material';
import { getFirestore, collection, getDocs, doc, updateDoc } from 'firebase/firestore';
import { getStorage, ref, getBlob, uploadBytes } from 'firebase/storage';
import {
    BACKFILL_TARGETS,
    generateVariantBlob,
    buildVariantPath,
    VARIANT_CONTENT_TYPE,
} from '../common/utils/imageUtils';
import { adminPageRootStyle } from './styles';

function PhotoVersionsBackfillPage() {
    const [running, setRunning] = useState(false);
    const [total, setTotal] = useState(0);
    const [done, setDone] = useState(0);
    const [skipped, setSkipped] = useState(0);
    const [errors, setErrors] = useState(0);
    const [log, setLog] = useState([]);
    const [regenerate, setRegenerate] = useState(false);
    const cancelRef = useRef(false);

    const append = (msg) => setLog((prev) => [...prev.slice(-400), msg]);

    const processTarget = async (target, firestore, storage) => {
        let docs = [];
        try {
            const snapshot = await getDocs(collection(firestore, target.collection));
            docs = snapshot.docs.map((d) => d.data());
        } catch (e) {
            append(`[${target.collection}] failed to load: ${e.message}`);
            return;
        }
        append(`[${target.collection}] loaded ${docs.length} docs`);
        setTotal((t) => t + docs.length);

        for (const item of docs) {
            if (cancelRef.current) return;

            const sourcePath = item[target.pathField];
            if (!item.id || !sourcePath) {
                append(`[${target.collection}] skip ${item.id || '(no id)'}: missing ${target.pathField}`);
                setSkipped((s) => s + 1);
                continue;
            }

            const todo = target.variants.filter((v) => regenerate || !item[v.field]);
            if (todo.length === 0) {
                append(`[${target.collection}] skip ${item.id}: variants present`);
                setSkipped((s) => s + 1);
                continue;
            }

            try {
                const originalBlob = await getBlob(ref(storage, sourcePath));
                const updates = {};
                const summary = [];
                for (const v of todo) {
                    const variantBlob = await generateVariantBlob(originalBlob, v);
                    const variantPath = buildVariantPath(v, item.id);
                    await uploadBytes(ref(storage, variantPath), variantBlob, { contentType: VARIANT_CONTENT_TYPE });
                    updates[v.field] = variantPath;
                    summary.push(`${v.key} ${Math.round(variantBlob.size / 1024)} KB`);
                }
                await updateDoc(doc(firestore, target.collection, item.id), updates);
                append(`[${target.collection}] ok ${item.id}: ${summary.join(', ')}`);
                setDone((d) => d + 1);
            } catch (e) {
                append(`[${target.collection}] err ${item.id}: ${e.message}`);
                setErrors((n) => n + 1);
            }
        }
    };

    const run = async () => {
        cancelRef.current = false;
        setRunning(true);
        setTotal(0);
        setDone(0);
        setSkipped(0);
        setErrors(0);
        setLog([]);

        const firestore = getFirestore();
        const storage = getStorage();

        for (const target of BACKFILL_TARGETS) {
            if (cancelRef.current) {
                append('Cancelled.');
                break;
            }
            await processTarget(target, firestore, storage);
        }

        setRunning(false);
    };

    const stop = () => {
        cancelRef.current = true;
    };

    const processed = done + skipped + errors;
    const progress = total === 0 ? 0 : Math.round((processed / total) * 100);

    return (
        <Paper style={adminPageRootStyle}>
            <h1>Generate image versions</h1>
            <Typography sx={{ mb: 2, maxWidth: 900 }}>
                Generates smaller JPEG versions (thumbnail 600px, medium 2000px) for every photo and every
                category / gallery cover, then writes the variant paths back to each Firestore document. Safe
                to re-run — items that already carry every variant are skipped unless the regenerate option
                is checked.
            </Typography>
            <Typography variant="body2" sx={{ mb: 2, maxWidth: 900, opacity: 0.7 }}>
                Collections processed: {BACKFILL_TARGETS.map((t) => t.collection).join(', ')}.
            </Typography>
            <FormControlLabel
                control={<Checkbox checked={regenerate} onChange={(e) => setRegenerate(e.target.checked)} disabled={running} />}
                label="Regenerate even if variants already exist"
                sx={{ mb: 2 }}
            />
            <Box sx={{ display: 'flex', gap: 1 }}>
                <Button variant="contained" onClick={run} disabled={running}>
                    {running ? 'Running…' : 'Start'}
                </Button>
                <Button variant="outlined" onClick={stop} disabled={!running}>
                    Stop
                </Button>
            </Box>
            {(running || total > 0) && (
                <Box sx={{ mt: 3, maxWidth: 1000 }}>
                    <Typography>
                        {processed} / {total} ({progress}%)
                    </Typography>
                    <LinearProgress variant="determinate" value={progress} sx={{ my: 1 }} />
                    <Typography variant="body2">
                        Created: {done} · Skipped: {skipped} · Errors: {errors}
                    </Typography>
                    <Box
                        sx={{
                            mt: 2,
                            fontFamily: 'monospace',
                            fontSize: 12,
                            maxHeight: 400,
                            overflow: 'auto',
                            bgcolor: 'rgba(0,0,0,0.04)',
                            p: 1,
                            border: '1px solid rgba(0,0,0,0.08)',
                        }}
                    >
                        {log.map((l, i) => (
                            <div key={i}>{l}</div>
                        ))}
                    </Box>
                </Box>
            )}
        </Paper>
    );
}

export default PhotoVersionsBackfillPage;
